import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '@/lib/api';
import { usePermissions } from '@/lib/roles';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ReactMarkdown from 'react-markdown';
import { CustomSelect } from '@/components/ui/custom-select';
import remarkGfm from 'remark-gfm';
import {
  ArrowLeft, Wand2, BarChart3, Edit3, Save, X, ExternalLink,
  Calendar, Target, Users, FileText, Plus,
  Link2, ChevronRight, ChevronDown, RefreshCw, TrendingUp
} from 'lucide-react';
import UseCaseSubmission from '@/components/UseCaseSubmission';

const MarkdownContent = ({ content }) => (
  <div className="prose prose-sm dark:prose-invert max-w-none text-[#30302F] dark:text-white font-light leading-relaxed prose-p:my-1 prose-ul:my-1 prose-li:my-0.5 prose-strong:text-[#30302F] dark:prose-strong:text-white">
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content || ''}</ReactMarkdown>
  </div>
);

const stateColors = {
  CAPTURED: 'bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]',
  INTAKE_COMPLETE: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/20 dark:text-cyan-400',
  REFINED: 'bg-purple-100 text-purple-700 dark:bg-purple-900/20 dark:text-purple-400',
  SCORED: 'bg-pink-100 text-pink-700 dark:bg-pink-900/20 dark:text-pink-400',
  SIZED: 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-400',
  PLANNED: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/20 dark:text-indigo-400',
  IN_PROGRESS: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400',
  DONE: 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400',
  DISCARDED: 'bg-gray-100 text-gray-500 dark:bg-gray-900/20 dark:text-gray-400',
  BLOCKED: 'bg-red-100 text-red-600 dark:bg-red-900/20 dark:text-red-400',
  TODO: 'bg-gray-100 text-gray-600 dark:bg-gray-900/20 dark:text-gray-400',
};
const storyStateColors = {
  TODO: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300',
  IN_PROGRESS: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  DONE: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  BLOCKED: 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-300',
};
const urgencyColors = {
  P0: 'bg-red-500 text-white', P1: 'bg-orange-500 text-white',
  P2: 'bg-yellow-400 text-yellow-900', P3: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
  P4: 'bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400',
};

const getItemType = (item) => {
  if (item.ado_type === 'Epic' || item.initiative_type === 'epic') return 'Initiative';
  if (item.ado_type === 'Feature' || item.initiative_type === 'feature') return 'Feature';
  return 'Item';
};

// Canonical Pilot states (mirrors backend VALID_STATES used in intake workflow).
const ITEM_STATE_OPTIONS = [
  'CAPTURED', 'INTAKE_COMPLETE', 'REFINED', 'SCORED', 'SIZED',
  'PLANNED', 'IN_PROGRESS', 'BLOCKED', 'DONE', 'DISCARDED',
];


export default function ItemDetail() {
  const can = usePermissions();
  const canEdit = can('editWork');
  const canAi = can('runAi');
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const autoOpenStoryId = searchParams.get('story');
  const [autoOpenedStoryId, setAutoOpenedStoryId] = useState(null);
  const [item, setItem] = useState(null);
  const [children, setChildren] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  const [refining, setRefining] = useState(false);
  const [refineResult, setRefineResult] = useState(null);
  const [selectedChild, setSelectedChild] = useState(null);
  const [childModalOpen, setChildModalOpen] = useState(false);
  const [childEditing, setChildEditing] = useState(false);
  const [childEditData, setChildEditData] = useState({});
  const [childSaving, setChildSaving] = useState(false);
  const [childRefining, setChildRefining] = useState(false);
  const [refineEdits, setRefineEdits] = useState(null);
  const [applyingRefine, setApplyingRefine] = useState(false);
  const [reparsing, setReparsing] = useState(false);
  const [reparseMessage, setReparseMessage] = useState(null);
  const [childRefineResult, setChildRefineResult] = useState(null);
  const [childRefineEdits, setChildRefineEdits] = useState(null);
  const [applyingChildRefine, setApplyingChildRefine] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState(new Set());
  const [syncing, setSyncing] = useState(false);
  const [allFeatures, setAllFeatures] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  
  // Create Feature/Story modal state
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState(null); // 'feature' or 'story'
  const [creating, setCreating] = useState(false);
  const [createData, setCreateData] = useState({
    title: '',
    problem: '',
    users_affected: '',
    success_criteria: '',
    urgency: 'P1',
    // Story-specific fields
    as_a: '',
    i_want: '',
    so_that: '',
    acceptance_criteria: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const [itemRes, childrenRes, productsRes, featuresRes, teamRes] = await Promise.all([
        api.get(`/intake/items/${itemId}`),
        api.get(`/intake/items/${itemId}/children`),
        api.get(`/products/`),
        api.get(`/intake/features`),
        api.get(`/intake/team-members`)
      ]);
      setItem(itemRes.data);
      setChildren(childrenRes.data);
      setAllProducts(productsRes.data || []);
      setAllFeatures(featuresRes.data || []);
      setTeamMembers(teamRes.data || []);
    } catch (err) {
      console.error('Failed to fetch item', err);
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open a child story modal when arriving with ?story=<id>. Used by the
  // Backlog page so clicking a story row jumps directly into the story modal
  // rather than parking the user on the parent feature's detail page.
  useEffect(() => {
    if (!autoOpenStoryId) return;
    if (autoOpenedStoryId === autoOpenStoryId) return;
    if (!children) return;
    // Look in both the feature's direct stories AND any stories nested
    // under child intakes (when the current item is an Epic).
    const direct = children.direct_stories || [];
    const nested = (children.child_intakes || []).flatMap((ci) => ci.stories || []);
    const story = [...direct, ...nested].find((s) => s.id === autoOpenStoryId);
    if (!story) return;
    setSelectedChild({ ...story, _type: 'story' });
    setChildEditing(false);
    setChildEditData({});
    setChildRefineResult(null);
    setChildRefineEdits(null);
    setChildModalOpen(true);
    setAutoOpenedStoryId(autoOpenStoryId);
    // Drop the query string so a manual modal close doesn't reopen the modal.
    setSearchParams({}, { replace: true });
  }, [autoOpenStoryId, autoOpenedStoryId, children, setSearchParams]);

  const startEdit = () => {
    setEditData({
      title: item.title, problem: item.problem, users_affected: item.users_affected,
      success_criteria: item.success_criteria, business_value: item.business_value || '',
      urgency: item.urgency, erd: item.erd || ''
    });
    setEditing(true);
  };

  const saveEdit = async () => {
    setSaving(true);
    try {
      await api.patch(`/intake/items/${itemId}`, editData);
      setEditing(false);
      fetchData();
    } catch (err) {
      console.error('Save failed', err);
    } finally { setSaving(false); }
  };

  const handleRefine = async () => {
    setRefining(true);
    try {
      const res = await api.post(`/ai/refine/${itemId}`);
      setRefineResult(res.data);
      setRefineEdits({
        problem: res.data.problem_statement || '',
        success_criteria: (res.data.success_criteria || []).join('\n'),
        users_affected: res.data.users_affected || ''
      });
    } catch (err) {
      console.error('Refine failed', err);
    } finally { setRefining(false); }
  };

  const handleApplyRefine = async () => {
    if (!refineEdits) return;
    setApplyingRefine(true);
    try {
      await api.post(`/ai/apply-refine/item/${itemId}`, refineEdits);
      setRefineResult(null);
      setRefineEdits(null);
      fetchData();
    } catch (err) {
      console.error('Apply refine failed', err);
    } finally { setApplyingRefine(false); }
  };

  const handleRICE = () => navigate(`/prioritize/${itemId}`);

  const handleReparse = async () => {
    setReparsing(true);
    setReparseMessage(null);
    try {
      const res = await api.post(`/devops/items/${itemId}/reparse`);
      const data = res.data || {};
      if (data.extracted && data.fields_updated?.length) {
        const labelMap = { problem: 'Problem', users_affected: 'Personas', success_criteria: 'Success Criteria', business_value: 'Business Value' };
        const userFields = data.fields_updated.filter(f => labelMap[f]).map(f => labelMap[f]);
        setReparseMessage({ type: 'ok', text: `Updated: ${userFields.join(', ')}` });
        fetchData();
      } else {
        setReparseMessage({ type: 'info', text: data.reason || 'Nothing to extract' });
      }
    } catch (err) {
      console.error('Reparse failed', err);
      setReparseMessage({ type: 'err', text: 'Re-parse failed. Check logs.' });
    } finally {
      setReparsing(false);
      setTimeout(() => setReparseMessage(null), 4000);
    }
  };

  const openChildModal = async (child, type) => {
    setSelectedChild({ ...child, _type: type });
    setChildEditing(false);
    setChildEditData({});
    setChildRefineResult(null);
    setChildRefineEdits(null);
    setChildModalOpen(true);
  };

  const startChildEdit = () => {
    const c = selectedChild;
    if (c._type === 'story') {
      setChildEditData({
        title: c.title, assignee: c.assignee || '', erd: c.erd || '',
        urgency: c.urgency || 'P1', state: c.state, intake_id: c.intake_id || '',
        story_points: c.story_points ?? '', t_shirt_size: c.t_shirt_size || '',
        rice_reach: c.rice_reach || '', rice_impact: c.rice_impact || '',
        rice_confidence: c.rice_confidence || '', rice_effort: c.rice_effort || ''
      });
    } else {
      setChildEditData({
        title: c.title, problem: c.problem || '', urgency: c.urgency || 'P1',
        erd: c.erd || '', success_criteria: c.success_criteria || '',
        assignee: c.assignee || '', product_id: c.product_id || ''
      });
    }
    setChildEditing(true);
  };

  const saveChildEdit = async () => {
    setChildSaving(true);
    try {
      if (selectedChild._type === 'story') {
        const payload = { ...childEditData };
        if (payload.rice_reach && payload.rice_impact && payload.rice_confidence && payload.rice_effort) {
          payload.rice_reach = Number(payload.rice_reach);
          payload.rice_impact = Number(payload.rice_impact);
          payload.rice_confidence = Number(payload.rice_confidence);
          payload.rice_effort = Number(payload.rice_effort);
          payload.rice_score = Math.round((payload.rice_reach * payload.rice_impact * payload.rice_confidence) / payload.rice_effort * 10) / 10;
        }
        await api.patch(`/intake/stories/${selectedChild.id}`, payload);
      } else {
        await api.patch(`/intake/items/${selectedChild.id}`, childEditData);
      }
      setChildEditing(false);
      setChildModalOpen(false);
      fetchData();
    } catch (err) {
      console.error('Child save failed', err);
    } finally { setChildSaving(false); }
  };

  const handleChildRefine = async () => {
    setChildRefining(true);
    try {
      if (selectedChild._type === 'story') {
        const res = await api.post(`/ai/refine-story/${selectedChild.id}`);
        setChildRefineResult(res.data);
        setChildRefineEdits({
          title: res.data.title || '',
          as_a: res.data.as_a || '',
          i_want: res.data.i_want || '',
          so_that: res.data.so_that || '',
          acceptance_criteria: (res.data.acceptance_criteria || []).join('\n')
        });
      } else {
        const res = await api.post(`/ai/refine/${selectedChild.id}`);
        setChildRefineResult(res.data);
        setChildRefineEdits({
          problem: res.data.problem_statement || '',
          success_criteria: (res.data.success_criteria || []).join('\n')
        });
      }
    } catch (err) {
      console.error('Child refine failed', err);
    } finally { setChildRefining(false); }
  };

  const handleApplyChildRefine = async () => {
    if (!childRefineEdits || !selectedChild) return;
    setApplyingChildRefine(true);
    try {
      if (selectedChild._type === 'story') {
        await api.post(`/ai/apply-refine/story/${selectedChild.id}`, {
          ...childRefineEdits,
          acceptance_criteria: childRefineEdits.acceptance_criteria
            ? childRefineEdits.acceptance_criteria.split('\n').filter(l => l.trim())
            : undefined
        });
      } else {
        await api.post(`/ai/apply-refine/item/${selectedChild.id}`, childRefineEdits);
      }
      setChildRefineResult(null);
      setChildRefineEdits(null);
      // Re-fetch data and update selectedChild in-place so the modal stays open
      const [itemRes, childrenRes] = await Promise.all([
        api.get(`/intake/items/${itemId}`),
        api.get(`/intake/items/${itemId}/children`),
      ]);
      setItem(itemRes.data);
      setChildren(childrenRes.data);
      // Refresh the selected child with updated data
      const allChildren = [
        ...(childrenRes.data.child_intakes || []).map(f => ({ ...f, _type: 'intake' })),
        ...(childrenRes.data.direct_stories || []).map(s => ({ ...s, _type: 'story' })),
      ];
      const updated = allChildren.find(c => c.id === selectedChild.id);
      if (updated) setSelectedChild(updated);
    } catch (err) {
      console.error('Apply child refine failed', err);
    } finally { setApplyingChildRefine(false); }
  };

  const handleSyncToADO = async () => {
    if (!selectedChild) return;
    setSyncing(true);
    try {
      const type = selectedChild._type === 'story' ? 'story' : 'intake';
      const res = await api.post(`/devops/push/${type}/${selectedChild.id}`);
      alert(res.data.message || 'Synced to ADO');
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.detail || err.message));
    } finally { setSyncing(false); }
  };

  const updatePriority = async (newPriority) => {
    try {
      await api.patch(`/intake/items/${itemId}`, { urgency: newPriority });
      fetchData();
    } catch (err) { console.error('Priority update failed', err); }
  };

  // Inline state change from header dropdown — optimistic update with rollback.
  const updateState = async (newState) => {
    if (!item || newState === item.state) return;
    const previous = item.state;
    setItem({ ...item, state: newState });
    try {
      await api.patch(`/intake/items/${itemId}`, { state: newState });
    } catch (err) {
      console.error('State update failed', err);
      setItem(prev => prev ? { ...prev, state: previous } : prev);
    }
  };

  // Tag chip add/remove — optimistic with rollback.
  const persistTags = async (nextTags, previousTags) => {
    try {
      await api.patch(`/intake/items/${itemId}`, { tags: nextTags });
    } catch (err) {
      console.error('Tags update failed', err);
      setItem(prev => prev ? { ...prev, tags: previousTags } : prev);
    }
  };

  const addTag = async (raw) => {
    if (!item) return;
    const value = (raw || '').trim();
    if (!value) return;
    const current = Array.isArray(item.tags) ? item.tags : [];
    if (current.includes(value)) return;
    const next = [...current, value];
    setItem({ ...item, tags: next });
    await persistTags(next, current);
  };

  const removeTag = async (tag) => {
    if (!item) return;
    const current = Array.isArray(item.tags) ? item.tags : [];
    const next = current.filter(t => t !== tag);
    setItem({ ...item, tags: next });
    await persistTags(next, current);
  };

  // Update an ADO mirror field (iteration_path / risk / remaining_work).
  const updateAdoMeta = async (field, value) => {
    if (!item) return;
    const previous = item[field];
    const optimisticValue = value === '' ? null : value;
    setItem({ ...item, [field]: optimisticValue });
    try {
      const payload = { [field]: field === 'remaining_work' && value !== '' && value !== null
        ? Number(value)
        : optimisticValue };
      await api.patch(`/intake/items/${itemId}`, payload);
    } catch (err) {
      console.error('ADO meta update failed', err);
      setItem(prev => prev ? { ...prev, [field]: previous } : prev);
    }
  };

  // Create Feature/Story functions
  const openCreateModal = (type) => {
    setCreateType(type);
    setCreateData({
      title: '',
      problem: '',
      users_affected: '',
      success_criteria: '',
      urgency: 'P1',
      as_a: '',
      i_want: '',
      so_that: '',
      acceptance_criteria: ''
    });
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!createData.title.trim()) return;
    setCreating(true);
    try {
      if (createType === 'feature') {
        // Create a Feature linked to this Epic
        await api.post('/intake/items', {
          title: createData.title,
          problem: createData.problem,
          users_affected: createData.users_affected,
          success_criteria: createData.success_criteria,
          urgency: createData.urgency,
          parent_id: item.id,  // Link to parent Epic
          initiative_type: 'feature',
          // Backlog work, not an intake request: kept out of the intake register.
          origin: 'item_detail',
          tags: ['feature'],
          product: item.product || '',
          product_id: item.product_id || null
        });
      } else if (createType === 'story') {
        // Create a Story linked to this Feature/Item
        await api.post('/sprints/stories', {
          intake_id: item.id,  // Link to parent Feature
          title: createData.title,
          as_a: createData.as_a,
          i_want: createData.i_want,
          so_that: createData.so_that,
          acceptance_criteria: createData.acceptance_criteria ? createData.acceptance_criteria.split('\n').filter(ac => ac.trim()) : [],
          risk_level: 'medium'
        });
      }
      setShowCreateModal(false);
      fetchData();
    } catch (err) {
      console.error('Create failed', err);
      alert('Failed to create: ' + (err.response?.data?.detail || err.message));
    } finally { setCreating(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="item-detail-loading">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#98139C] border-t-transparent" />
    </div>
  );
  if (!item) return <div className="text-center py-20 text-gray-400" data-testid="item-not-found">Item not found</div>;

  const itemType = getItemType(item);
  const childIntakes = children?.child_intakes || [];
  const directStories = children?.direct_stories || [];

  // Build grouped structure: features with nested stories, plus standalone stories
  const featureGroups = childIntakes.map(ci => ({
    ...ci, _rowType: 'intake', _childType: ci.ado_type || 'Feature',
    _stories: (ci.stories || []).map(s => ({ ...s, _rowType: 'story', _childType: 'Story' }))
  }));
  const standaloneStories = directStories.map(s => ({ ...s, _rowType: 'story', _childType: 'Story' }));
  const totalChildren = featureGroups.reduce((n, f) => n + 1 + f._stories.length, 0) + standaloneStories.length;

  const toggleFeature = (featureId) => {
    setExpandedFeatures(prev => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId); else next.add(featureId);
      return next;
    });
  };
  const expandAll = () => setExpandedFeatures(new Set(featureGroups.map(f => f.id)));
  const collapseAll = () => setExpandedFeatures(new Set());

  return (
    <div className="p-8 max-w-[1400px]" data-testid="item-detail-page">
      {/* Page Header — matching Dashboard/Backlog pattern */}
      <div className="mb-10" data-testid="item-detail-header">
        <button onClick={() => navigate('/backlog')} className="flex items-center gap-1.5 text-sm text-[#98139C] hover:text-[#4F0077] transition-colors font-medium mb-4" data-testid="back-to-backlog">
          <ArrowLeft size={16} /> Back to Backlog
        </button>

        <div className="flex items-center gap-2 mb-2">
          <p className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#98139C]">{itemType}</p>
          <select
            value={item.state || 'CAPTURED'}
            onChange={(e) => updateState(e.target.value)}
            disabled={!canEdit}
            className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 border-0 appearance-none cursor-pointer focus:outline-none focus:ring-1 focus:ring-[#98139C] ${stateColors[item.state] || 'bg-gray-100 text-gray-600'}`}
            data-testid="item-state-select"
            aria-label="Change item state"
          >
            {(ITEM_STATE_OPTIONS.includes(item.state) ? ITEM_STATE_OPTIONS : [item.state, ...ITEM_STATE_OPTIONS].filter(Boolean)).map(s => (
              <option key={s} value={s} className="bg-white dark:bg-[#1a1a2e] text-[#30302F] dark:text-white">
                {s.replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          {item.intake_ref && (
            <span className="text-xs text-[#30302F] dark:text-white font-mono" data-testid="item-intake-ref">{item.intake_ref}</span>
          )}
          {item.ado_id && (
            <span className="text-xs text-[#30302F] dark:text-white font-mono">#{item.ado_id}</span>
          )}
        </div>

        {editing ? (
          <input value={editData.title} onChange={e => setEditData({...editData, title: e.target.value})} className="w-full text-4xl font-heading bg-transparent border-b-2 border-[#98139C]/30 pb-1 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.8px' }} data-testid="edit-title" />
        ) : (
          <h1 className="text-4xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.8px' }} data-testid="item-title">{item.title}</h1>
        )}

        <div className="flex items-center justify-between mt-3">
          {/* Meta info + Priority */}
          <div className="flex items-center gap-4 text-sm text-[#30302F] dark:text-white font-light">
            {item.product && <span data-testid="item-product">{item.product}</span>}
            {item.created_at && <span>{new Date(item.created_at).toLocaleDateString()}</span>}
            <div className="flex items-center gap-1.5" data-testid="priority-pills">
              {['P0','P1','P2','P3','P4'].map(p => (
                <button key={p} onClick={() => canEdit && !editing && updatePriority(p)} disabled={!canEdit}
                  className={`px-2.5 py-1 text-[11px] font-bold transition-all ${item.urgency === p ? urgencyColors[p] : 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 hover:opacity-80'}`}
                  data-testid={`priority-${p}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3" data-testid="action-buttons">
            {!canEdit ? null : !editing ? (
              <button onClick={startEdit} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="edit-btn">
                <Edit3 size={14} /> Edit
              </button>
            ) : (
              <>
                <button onClick={saveEdit} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="save-btn">
                  <Save size={14} /> {saving ? 'Saving...' : 'Save'}
                </button>
                <button onClick={() => setEditing(false)} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-red-300 text-[#30302F] dark:text-white transition-colors" data-testid="cancel-btn">
                  <X size={14} /> Cancel
                </button>
              </>
            )}
            {canAi && <button onClick={handleRefine} disabled={refining} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="refine-btn">
              <Wand2 size={14} /> {refining ? 'Refining...' : 'Refine'}
            </button>}
            {canAi && item.ado_imported && (
              <button onClick={handleReparse} disabled={reparsing} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white disabled:opacity-50" data-testid="reparse-btn" title="Run the AI extractor on the ADO description and populate structured fields verbatim.">
                <RefreshCw size={14} className={reparsing ? 'animate-spin' : ''} /> {reparsing ? 'Re-parsing...' : 'Re-parse from ADO'}
              </button>
            )}
            <button onClick={handleRICE} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] dark:hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="rice-btn">
              <BarChart3 size={14} /> RICE Score
            </button>
            {item.ado_url && (
              <a href={item.ado_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="ado-link">
                <ExternalLink size={14} /> ADO
              </a>
            )}
          </div>
          {reparseMessage && (
            <div className={`mt-2 text-xs ${reparseMessage.type === 'ok' ? 'text-emerald-600 dark:text-emerald-400' : reparseMessage.type === 'err' ? 'text-red-600 dark:text-red-400' : 'text-[#30302F]/60 dark:text-white/60'}`} data-testid="reparse-msg">
              {reparseMessage.text}
            </div>
          )}
        </div>
      </div>

      {/* Overview — main column carries the long-form content (Problem,
          Success Criteria) so it gets the reading width it needs. The
          sidebar parks short metadata (Personas, ERD) without competing
          for attention. */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10" data-testid="overview-cards">
        {/* Main column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Problem Statement */}
          <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6" data-testid="card-problem">
            <div className="flex items-center gap-2 mb-3">
              <FileText size={16} strokeWidth={1.5} className="text-[#98139C]" />
              <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Problem Statement</h2>
            </div>
            {editing ? (
              <textarea value={editData.problem} onChange={e => setEditData({...editData, problem: e.target.value})}
                className="w-full min-h-[140px] text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-y font-light"
                placeholder="Describe the problem... (supports **markdown**)" data-testid="edit-problem" />
            ) : (
              <div className="text-sm text-[#30302F] dark:text-white leading-relaxed">
                {item.problem ? <MarkdownContent content={item.problem} /> : <span className="text-[#30302F]/50 dark:text-white/50 italic">Not specified</span>}
              </div>
            )}
          </section>

          {/* Success Criteria */}
          <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6" data-testid="card-success">
            <div className="flex items-center gap-2 mb-3">
              <Target size={16} strokeWidth={1.5} className="text-[#f59e0b]" />
              <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Success Criteria</h2>
            </div>
            {editing ? (
              <textarea value={editData.success_criteria} onChange={e => setEditData({...editData, success_criteria: e.target.value})}
                className="w-full min-h-[140px] text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-y font-light"
                placeholder="Define success... (supports **markdown**)" data-testid="edit-success" />
            ) : (
              <div className="text-sm text-[#30302F] dark:text-white leading-relaxed">
                {item.success_criteria ? <MarkdownContent content={item.success_criteria} /> : <span className="text-[#30302F]/50 dark:text-white/50 italic">Not defined</span>}
              </div>
            )}
          </section>

          {/* Business Value — only when present (e.g., Yggdrasil epics) so non-Yggdrasil items don't carry an empty card. */}
          {(editing || item.business_value) && (
            <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-6" data-testid="card-business-value">
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={16} strokeWidth={1.5} className="text-[#10b981]" />
                <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Business Value</h2>
              </div>
              {editing ? (
                <textarea value={editData.business_value || ''} onChange={e => setEditData({...editData, business_value: e.target.value})}
                  className="w-full min-h-[120px] text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-y font-light"
                  placeholder="Prevention / enablement / audit value... (supports **markdown**)" data-testid="edit-business-value" />
              ) : (
                <div className="text-sm text-[#30302F] dark:text-white leading-relaxed">
                  <MarkdownContent content={item.business_value} />
                </div>
              )}
            </section>
          )}

          {/* Full portal submission (AI use case items only) */}
          <UseCaseSubmission item={item} />
        </div>

        {/* Sidebar */}
        <aside className="space-y-6">
          {/* User Personas */}
          <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-5" data-testid="card-personas">
            <div className="flex items-center gap-2 mb-3">
              <Users size={16} strokeWidth={1.5} className="text-[#8b5cf6]" />
              <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">User Personas</h2>
            </div>
            {editing ? (
              <textarea value={editData.users_affected} onChange={e => setEditData({...editData, users_affected: e.target.value})}
                className="w-full min-h-[110px] text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 rounded-lg p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-y font-light"
                placeholder="Who is affected..." data-testid="edit-personas" />
            ) : (
              <div className="text-sm text-[#30302F] dark:text-white leading-relaxed">
                {item.users_affected ? <MarkdownContent content={item.users_affected} /> : <span className="text-[#30302F]/50 dark:text-white/50 italic">Not specified</span>}
              </div>
            )}
          </section>

          {/* Estimated Release Date */}
          <section className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 rounded-xl p-5" data-testid="card-erd">
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={16} strokeWidth={1.5} className="text-[#10b981]" />
              <h2 className="text-[11px] uppercase tracking-[0.9px] font-bold text-[#30302F] dark:text-white">Est. Release Date</h2>
            </div>
            {editing ? (
              <input type="date" value={editData.erd || ''} onChange={e => setEditData({...editData, erd: e.target.value})}
                className="w-full text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 rounded-lg p-2.5 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white" data-testid="edit-erd" />
            ) : item.erd ? (
              <div>
                <div className="text-2xl font-heading text-[#10b981]" data-testid="erd-date">
                  {new Date(item.erd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </div>
                <div className="text-xs text-[#30302F]/70 dark:text-white/60 mt-1 font-light">
                  Q{Math.ceil((new Date(item.erd).getMonth() + 1) / 3)} {new Date(item.erd).getFullYear()}
                </div>
                <button onClick={() => navigate(`/roadmap?highlight=${item.id}`)} className="mt-3 text-[11px] text-[#98139C] hover:text-[#4F0077] font-medium inline-flex items-center gap-1" data-testid="view-on-roadmap">
                  <Link2 size={12} /> View on Roadmap
                </button>
              </div>
            ) : (
              <span className="text-sm text-[#30302F]/50 dark:text-white/50 italic">Not set</span>
            )}
          </section>
        </aside>
      </div>

      {/* Tags Card */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-6 mb-10" data-testid="tags-card">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium text-[#30302F] dark:text-white">Tags</span>
          <span className="text-xs text-[#30302F] dark:text-white font-light">{(item.tags || []).length}</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {(item.tags || []).map(tag => (
            <span
              key={tag}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] uppercase tracking-wider font-semibold bg-[#f0dcf0] text-[#4F0077] dark:bg-[#98139C]/20 dark:text-[#d87bdb]"
              data-testid={`tag-chip-${tag}`}
            >
              {tag}
              {canEdit && <button
                onClick={() => removeTag(tag)}
                className="hover:text-red-600 dark:hover:text-red-400 transition-colors"
                aria-label={`Remove tag ${tag}`}
                data-testid={`tag-remove-${tag}`}
              >
                <X size={12} />
              </button>}
            </span>
          ))}
          {canEdit && <input
            type="text"
            placeholder="Add tag…"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addTag(e.target.value);
                e.target.value = '';
              }
            }}
            onBlur={(e) => {
              if (e.target.value.trim()) {
                addTag(e.target.value);
                e.target.value = '';
              }
            }}
            className="text-[11px] uppercase tracking-wider font-medium bg-transparent border-b border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white px-2 py-1 min-w-[120px]"
            data-testid="tag-input"
          />}
        </div>
      </div>

      {/* AI Refine Result — Editable */}
      {refineResult && refineEdits && (
        <div className="bg-white dark:bg-white/5 border border-[#98139C]/30 p-6 mb-10 space-y-4" data-testid="refine-result">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Wand2 size={16} className="text-[#98139C]" />
              <span className="text-sm font-heading font-semibold text-[#30302F] dark:text-white">AI Suggestions</span>
            </div>
            <div className="flex items-center gap-2">
              {canAi && <button onClick={handleApplyRefine} disabled={applyingRefine}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="apply-refine-btn">
                <Save size={12} /> {applyingRefine ? 'Applying...' : 'Apply'}
              </button>}
              <button onClick={() => { setRefineResult(null); setRefineEdits(null); }}
                className="text-[#30302F] hover:text-red-500 dark:text-white dark:hover:text-red-400 transition-colors" data-testid="dismiss-refine-btn"><X size={16} /></button>
            </div>
          </div>

          {/* Scope alert for epics */}
          {refineResult.scope_assessment && refineResult.scope_assessment !== 'correctly_scoped' && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 p-3 text-sm" data-testid="scope-alert">
              <span className="font-medium text-yellow-800 dark:text-yellow-300">Scope Warning: </span>
              <span className="text-yellow-700 dark:text-yellow-400">
                This item may be better as a {refineResult.scope_assessment === 'should_be_feature' ? 'Feature' : 'Story'}.
                {refineResult.scope_rationale && ` ${refineResult.scope_rationale}`}
              </span>
            </div>
          )}

          {/* Editable Problem Statement */}
          <div>
            <span className="text-sm font-medium text-[#30302F] dark:text-white">Suggested Problem Statement</span>
            <textarea value={refineEdits.problem} onChange={e => setRefineEdits({...refineEdits, problem: e.target.value})}
              className="w-full min-h-[80px] mt-2 text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-none font-light"
              data-testid="refine-edit-problem" />
          </div>

          {/* Editable Success Criteria */}
          <div>
            <span className="text-sm font-medium text-[#30302F] dark:text-white">Suggested Success Criteria (one per line)</span>
            <textarea value={refineEdits.success_criteria} onChange={e => setRefineEdits({...refineEdits, success_criteria: e.target.value})}
              className="w-full min-h-[60px] mt-2 text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-none font-light"
              data-testid="refine-edit-criteria" />
          </div>

          {/* Editable Users Affected */}
          <div>
            <span className="text-sm font-medium text-[#30302F] dark:text-white">Suggested Users Affected</span>
            <textarea value={refineEdits.users_affected} onChange={e => setRefineEdits({...refineEdits, users_affected: e.target.value})}
              className="w-full min-h-[40px] mt-2 text-sm bg-transparent border border-[#e3e3e3] dark:border-white/10 p-3 focus:outline-none focus:border-[#98139C] text-[#30302F] dark:text-white resize-none font-light"
              data-testid="refine-edit-users" />
          </div>

          {/* Read-only: Child suggestions (epics) */}
          {refineResult.child_suggestions?.length > 0 && (
            <div>
              <span className="text-sm font-medium text-[#30302F] dark:text-white">Child Suggestions</span>
              <div className="mt-2 space-y-1">{refineResult.child_suggestions.map((cs, i) => (
                <div key={i} className="text-sm text-[#30302F] dark:text-white font-light">
                  <strong>{cs.child_title}</strong> ({cs.child_type}) — {cs.suggestion}
                </div>
              ))}</div>
            </div>
          )}

          {/* Read-only: Proposed Stories */}
          {refineResult.proposed_stories?.length > 0 && (
            <div>
              <span className="text-sm font-medium text-[#30302F] dark:text-white">Proposed Stories ({refineResult.proposed_stories.length})</span>
              <div className="mt-2 space-y-1">{refineResult.proposed_stories.map((s, i) => <div key={i} className="text-sm text-[#30302F] dark:text-white font-light">{i+1}. <strong>{s.title}</strong> — {s.description}</div>)}</div>
            </div>
          )}

          {refineResult.refinement_notes && <p className="text-xs text-[#30302F] dark:text-white italic font-light">{refineResult.refinement_notes}</p>}
        </div>
      )}

      {/* Children Table — nested with expand/collapse */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10" data-testid="children-table-section">
        <div className="p-6 border-b border-[#e3e3e3] dark:border-white/10 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-heading text-[#30302F] dark:text-white">
              {itemType === 'Initiative' ? 'Features & Stories' : 'User Stories'}
            </h2>
            <p className="text-sm text-[#30302F] dark:text-white mt-1">{totalChildren} items</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Create Feature button - only for Epics/Initiatives */}
            {canEdit && itemType === 'Initiative' && (
              <button 
                onClick={() => openCreateModal('feature')} 
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors"
                data-testid="create-feature-btn"
              >
                <Plus size={14} /> Create Feature
              </button>
            )}
            {/* Create Story button - for Features and Items */}
            {canEdit && (itemType === 'Feature' || itemType === 'Item') && (
              <button 
                onClick={() => openCreateModal('story')} 
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors"
                data-testid="create-story-btn"
              >
                <Plus size={14} /> Create Story
              </button>
            )}
            {featureGroups.length > 0 && (
              <div className="flex items-center gap-1">
                <button onClick={expandAll} className="text-xs text-[#30302F] dark:text-white border border-[#e3e3e3] dark:border-white/10 px-3 py-1.5 rounded-full hover:border-[#98139C] transition-colors" data-testid="expand-all-btn">Expand All</button>
                <button onClick={collapseAll} className="text-xs text-[#30302F] dark:text-white border border-[#e3e3e3] dark:border-white/10 px-3 py-1.5 rounded-full hover:border-[#98139C] transition-colors" data-testid="collapse-all-btn">Collapse All</button>
              </div>
            )}
            <button onClick={() => navigate(`/roadmap?highlight=${item.id}`)} className="text-sm text-[#98139C] hover:underline font-medium flex items-center gap-1" data-testid="view-roadmap-link">
              View on Roadmap <ExternalLink size={14} />
            </button>
          </div>
        </div>

        {totalChildren === 0 ? (
          <div className="p-6 py-16 text-center">
            <FileText size={32} className="mx-auto mb-2 text-[#30302F] dark:text-white" />
            <p className="text-[#30302F] dark:text-white font-heading">No child items yet</p>
            <p className="text-sm text-[#30302F] dark:text-white mt-1">
              {itemType === 'Initiative' 
                ? 'Click "Create Feature" to add a feature, or "Refine" to auto-generate stories.'
                : 'Click "Create Story" to add a story, or "Refine" to auto-generate stories.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="children-table">
              <thead>
                <tr className="border-b border-[#e3e3e3] dark:border-white/10">
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white">Title</th>
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white w-24">Type</th>
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white w-36">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white w-28">ERD</th>
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white w-28">Sprint</th>
                  <th className="text-left p-4 text-sm font-medium text-[#30302F] dark:text-white w-16">ADO</th>
                </tr>
              </thead>
              <tbody>
                {/* Feature groups with nested stories */}
                {featureGroups.map(feature => {
                  const isExpanded = expandedFeatures.has(feature.id);
                  const featureState = feature.state || 'CAPTURED';
                  return [
                    <tr key={feature.id}
                      className="border-b border-[#e3e3e3]/50 dark:border-white/5 hover:bg-[#faf8fb] dark:hover:bg-white/[0.02] cursor-pointer transition-colors"
                      data-testid={`child-row-${feature.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          {feature._stories.length > 0 && (
                            <button onClick={(e) => { e.stopPropagation(); toggleFeature(feature.id); }} className="text-[#30302F] dark:text-white hover:text-[#98139C] transition-colors flex-shrink-0" data-testid={`toggle-${feature.id}`}>
                              {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </button>
                          )}
                          <span className="text-sm font-medium text-[#30302F] dark:text-white" onClick={() => openChildModal(feature, 'intake')}>{feature.title}</span>
                          {feature._stories.length > 0 && (
                            <span className="text-xs text-[#30302F] dark:text-white">{feature._stories.length} stories</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4" onClick={() => openChildModal(feature, 'intake')}>
                        <span className="text-xs font-medium text-[#98139C]">{feature._childType}</span>
                      </td>
                      <td className="p-4" onClick={() => openChildModal(feature, 'intake')}>
                        <Badge className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 ${stateColors[featureState] || 'bg-gray-100 text-gray-500'}`}>
                          {featureState.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-[#30302F] dark:text-white" onClick={() => openChildModal(feature, 'intake')}>
                        {feature.erd ? new Date(feature.erd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                      </td>
                      <td className="p-4 text-sm text-[#30302F] dark:text-white" onClick={() => openChildModal(feature, 'intake')}>—</td>
                      <td className="p-4" onClick={() => openChildModal(feature, 'intake')}>
                        {feature.ado_url ? (
                          <a href={feature.ado_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#98139C] hover:text-[#4F0077]" data-testid={`ado-link-${feature.id}`}>
                            <ExternalLink size={14} />
                          </a>
                        ) : feature.ado_id ? (
                          <span className="text-xs text-[#30302F] dark:text-white font-mono">#{feature.ado_id}</span>
                        ) : <span className="text-[#30302F]">—</span>}
                      </td>
                    </tr>,
                    ...(isExpanded ? feature._stories.map(story => {
                      const storyState = story.state || 'TODO';
                      return (
                        <tr key={story.id}
                          onClick={() => openChildModal(story, 'story')}
                          className="border-b border-[#e3e3e3]/30 dark:border-white/[0.03] hover:bg-[#faf8fb] dark:hover:bg-white/[0.02] cursor-pointer transition-colors bg-[#fafafa] dark:bg-white/[0.01]"
                          data-testid={`child-row-${story.id}`}>
                          <td className="p-4 pl-12">
                            <span className="text-sm text-[#30302F] dark:text-white">{story.title}</span>
                          </td>
                          <td className="p-4">
                            <span className="text-xs text-[#30302F] dark:text-white">Story</span>
                          </td>
                          <td className="p-4">
                            <Badge className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 ${storyStateColors[storyState] || 'bg-gray-100 text-gray-500'}`}>
                              {storyState.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="p-4 text-sm text-[#30302F] dark:text-white">
                            {story.erd ? new Date(story.erd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                          </td>
                          <td className="p-4 text-sm text-[#30302F] dark:text-white">{story.sprint_name || '—'}</td>
                          <td className="p-4">
                            {story.ado_url ? (
                              <a href={story.ado_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#98139C] hover:text-[#4F0077]">
                                <ExternalLink size={14} />
                              </a>
                            ) : story.ado_story_id ? (
                              <span className="text-xs text-[#30302F] dark:text-white font-mono">#{story.ado_story_id}</span>
                            ) : <span className="text-[#30302F]">—</span>}
                          </td>
                        </tr>
                      );
                    }) : [])
                  ];
                })}
                {/* Standalone stories (no parent feature) */}
                {standaloneStories.map(story => {
                  const storyState = story.state || 'TODO';
                  return (
                    <tr key={story.id}
                      onClick={() => openChildModal(story, 'story')}
                      className="border-b border-[#e3e3e3]/50 dark:border-white/5 hover:bg-[#faf8fb] dark:hover:bg-white/[0.02] cursor-pointer transition-colors"
                      data-testid={`child-row-${story.id}`}>
                      <td className="p-4"><span className="text-sm text-[#30302F] dark:text-white">{story.title}</span></td>
                      <td className="p-4"><span className="text-xs text-[#30302F] dark:text-white">Story</span></td>
                      <td className="p-4">
                        <Badge className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 ${storyStateColors[storyState] || 'bg-gray-100 text-gray-500'}`}>
                          {storyState.replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="p-4 text-sm text-[#30302F] dark:text-white">{story.erd ? new Date(story.erd).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}</td>
                      <td className="p-4 text-sm text-[#30302F] dark:text-white">{story.sprint_name || '—'}</td>
                      <td className="p-4">
                        {story.ado_url ? (
                          <a href={story.ado_url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} className="text-[#98139C] hover:text-[#4F0077]">
                            <ExternalLink size={14} />
                          </a>
                        ) : story.ado_story_id ? (
                          <span className="text-xs text-[#30302F] dark:text-white font-mono">#{story.ado_story_id}</span>
                        ) : <span className="text-[#30302F]">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Child Item Detail Modal */}
      <Dialog open={childModalOpen} onOpenChange={setChildModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 p-0 rounded-xl" data-testid="child-modal">
          {selectedChild && (
            <div className="p-6">
              <DialogHeader className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 ${selectedChild._type === 'story' ? 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300' : 'bg-[#f0dcf0] text-[#4F0077]'}`}>
                    {selectedChild._type === 'story' ? 'Story' : (selectedChild.ado_type || 'Feature')}
                  </Badge>
                  <Badge className={`rounded-none text-[11px] uppercase tracking-wider font-semibold px-2 py-0.5 ${(selectedChild._type === 'story' ? storyStateColors : stateColors)[selectedChild.state] || 'bg-gray-100 text-gray-500'}`}>
                    {(selectedChild.state || 'TODO').replace(/_/g, ' ')}
                  </Badge>
                  {(selectedChild.ado_id || selectedChild.ado_story_id) && (
                    <span className="text-xs text-[#30302F] dark:text-white font-mono">#{selectedChild.ado_id || selectedChild.ado_story_id}</span>
                  )}
                </div>
                <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.4px' }}>
                  {childEditing ? (
                    <input value={childEditData.title} onChange={e => setChildEditData({...childEditData, title: e.target.value})}
                      className="w-full bg-transparent border-b-2 border-[#98139C]/30 pb-1 focus:outline-none focus:border-[#98139C] text-xl font-heading" data-testid="child-edit-title" />
                  ) : selectedChild.title}
                </DialogTitle>
              </DialogHeader>

              {/* Action buttons */}
              <div className="flex items-center gap-2 flex-wrap mb-6" data-testid="child-modal-actions">
                {!canEdit ? null : !childEditing ? (
                  <button onClick={startChildEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="child-edit-btn">
                    <Edit3 size={12} /> Edit
                  </button>
                ) : (
                  <>
                    <button onClick={saveChildEdit} disabled={childSaving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="child-save-btn">
                      <Save size={12} /> {childSaving ? 'Saving...' : 'Save'}
                    </button>
                    <button onClick={() => setChildEditing(false)} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-red-300 transition-colors text-[#30302F] dark:text-white" data-testid="child-cancel-btn">
                      <X size={12} /> Cancel
                    </button>
                  </>
                )}
                {canAi && <button onClick={handleChildRefine} disabled={childRefining} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="child-refine-btn">
                  <Wand2 size={12} /> {childRefining ? 'Refining...' : 'Refine'}
                </button>}
                {canEdit && <button onClick={handleSyncToADO} disabled={syncing} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#98139C] text-[#98139C] rounded-full hover:bg-[#98139C]/10 transition-colors" data-testid="child-sync-ado-btn">
                  <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} /> {syncing ? 'Pushing...' : 'Push to ADO'}
                </button>}
                {selectedChild._type === 'intake' && (
                  <button onClick={() => { setChildModalOpen(false); navigate(`/backlog/${selectedChild.id}`); }} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="child-open-detail">
                    <ExternalLink size={12} /> Open Detail
                  </button>
                )}
                {selectedChild.ado_url && (
                  <a href={selectedChild.ado_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white" data-testid="child-ado-link">
                    <ExternalLink size={12} /> Open in ADO
                  </a>
                )}
              </div>

              {/* Child Refinement UI */}
              {childRefineResult && childRefineEdits && (
                <div className="border border-[#8b5cf6]/40 bg-[#faf5ff] dark:bg-[#8b5cf6]/10 p-4 mb-6 space-y-3" data-testid="child-refine-result">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Wand2 size={14} className="text-[#8b5cf6]" />
                      <span className="text-sm font-heading font-semibold text-[#30302F] dark:text-white">AI Suggestions</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {canAi && <button onClick={handleApplyChildRefine} disabled={applyingChildRefine}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors" data-testid="apply-child-refine-btn">
                        <Save size={12} /> {applyingChildRefine ? 'Applying...' : 'Apply'}
                      </button>}
                      <button onClick={() => { setChildRefineResult(null); setChildRefineEdits(null); }}
                        className="text-[#30302F] hover:text-red-500 dark:text-white dark:hover:text-red-400 transition-colors" data-testid="dismiss-child-refine-btn"><X size={14} /></button>
                    </div>
                  </div>

                  {selectedChild._type === 'story' ? (
                    <>
                      <div>
                        <span className="text-xs font-medium text-[#30302F] dark:text-white">Title</span>
                        <input value={childRefineEdits.title} onChange={e => setChildRefineEdits({...childRefineEdits, title: e.target.value})}
                          className="w-full mt-1 h-8 px-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                          data-testid="child-refine-title" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <span className="text-xs font-medium text-[#30302F] dark:text-white">As a...</span>
                          <input value={childRefineEdits.as_a} onChange={e => setChildRefineEdits({...childRefineEdits, as_a: e.target.value})}
                            className="w-full mt-1 h-8 px-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                            data-testid="child-refine-asa" />
                        </div>
                        <div>
                          <span className="text-xs font-medium text-[#30302F] dark:text-white">I want...</span>
                          <input value={childRefineEdits.i_want} onChange={e => setChildRefineEdits({...childRefineEdits, i_want: e.target.value})}
                            className="w-full mt-1 h-8 px-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                            data-testid="child-refine-iwant" />
                        </div>
                        <div>
                          <span className="text-xs font-medium text-[#30302F] dark:text-white">So that...</span>
                          <input value={childRefineEdits.so_that} onChange={e => setChildRefineEdits({...childRefineEdits, so_that: e.target.value})}
                            className="w-full mt-1 h-8 px-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                            data-testid="child-refine-sothat" />
                        </div>
                      </div>
                      <div>
                        <span className="text-xs font-medium text-[#30302F] dark:text-white">Acceptance Criteria (one per line)</span>
                        <textarea value={childRefineEdits.acceptance_criteria} onChange={e => setChildRefineEdits({...childRefineEdits, acceptance_criteria: e.target.value})}
                          className="w-full min-h-[60px] mt-1 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-2 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none font-mono"
                          data-testid="child-refine-ac" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <span className="text-xs font-medium text-[#30302F] dark:text-white">Problem Statement</span>
                        <textarea value={childRefineEdits.problem} onChange={e => setChildRefineEdits({...childRefineEdits, problem: e.target.value})}
                          className="w-full min-h-[60px] mt-1 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-2 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none"
                          data-testid="child-refine-problem" />
                      </div>
                      <div>
                        <span className="text-xs font-medium text-[#30302F] dark:text-white">Success Criteria (one per line)</span>
                        <textarea value={childRefineEdits.success_criteria} onChange={e => setChildRefineEdits({...childRefineEdits, success_criteria: e.target.value})}
                          className="w-full min-h-[60px] mt-1 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-2 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none"
                          data-testid="child-refine-criteria" />
                      </div>
                    </>
                  )}

                  {childRefineResult.refinement_notes && (
                    <p className="text-xs text-[#30302F] dark:text-white italic font-light">{childRefineResult.refinement_notes}</p>
                  )}
                </div>
              )}

              {/* Content Grid */}
              <div className="grid grid-cols-2 gap-6">
                {/* Left: Content */}
                <div className="space-y-4">
                  {/* Description / Problem */}
                  <div>
                    <span className="text-sm font-medium text-[#30302F] dark:text-white">Description</span>
                    {childEditing && selectedChild._type === 'intake' ? (
                      <textarea value={childEditData.problem} onChange={e => setChildEditData({...childEditData, problem: e.target.value})}
                        className="w-full min-h-[100px] mt-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none" data-testid="child-edit-problem" />
                    ) : (
                      <div className="mt-2 text-sm text-[#30302F] dark:text-white leading-relaxed">
                        {selectedChild._type === 'intake' ? (
                          selectedChild.problem ? <MarkdownContent content={selectedChild.problem} /> : <span className="text-[#30302F] dark:text-white italic">Not specified</span>
                        ) : (
                          <>
                            {/* Prefer the canonical As-a/I-want/so-that form
                                when present (most stories have it after the
                                ADO import parser). Fall back to the raw
                                description only when it isn't. Avoids
                                duplicating the same content twice. */}
                            {selectedChild.as_a && selectedChild.as_a.trim() ? (
                              <p className="leading-relaxed">
                                As a <strong>{selectedChild.as_a}</strong>, I want <strong>{selectedChild.i_want}</strong>
                                {selectedChild.so_that ? <>, so that <strong>{selectedChild.so_that}</strong></> : null}
                              </p>
                            ) : selectedChild.description ? (
                              <div className="leading-relaxed">
                                <MarkdownContent content={selectedChild.description.replace(/<[^>]*>/g, '')} />
                              </div>
                            ) : (
                              <span className="text-[#30302F]/50 dark:text-white/50 italic">No description available. Use Refine to generate a user story.</span>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Success Criteria / Acceptance Criteria */}
                  <div>
                    <span className="text-sm font-medium text-[#30302F] dark:text-white">
                      {selectedChild._type === 'intake' ? 'Success Criteria' : 'Acceptance Criteria'}
                    </span>
                    {childEditing && selectedChild._type === 'intake' ? (
                      <textarea value={childEditData.success_criteria} onChange={e => setChildEditData({...childEditData, success_criteria: e.target.value})}
                        className="w-full min-h-[80px] mt-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-3 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none" data-testid="child-edit-success" />
                    ) : (
                      <div className="mt-2 text-sm text-[#30302F] dark:text-white leading-relaxed">
                        {selectedChild._type === 'intake' ? (
                          selectedChild.success_criteria ? <MarkdownContent content={selectedChild.success_criteria} /> : <span className="text-[#30302F] dark:text-white italic">Not defined</span>
                        ) : (
                          selectedChild.acceptance_criteria?.length > 0 ? (
                            <ul className="space-y-3">{selectedChild.acceptance_criteria.map((c, i) => (
                              <li key={i} className="flex items-start gap-2 border-l-2 border-green-200 dark:border-green-700/40 pl-3 py-1">
                                <span className="text-green-500 mt-0.5 shrink-0">✓</span>
                                <div className="flex-1 min-w-0">
                                  {/* AC items often contain markdown like
                                      "**Given** X **When** Y **Then** Z" so
                                      render through the same component as
                                      the rest of the modal for bold + lists. */}
                                  <MarkdownContent content={typeof c === 'string' ? c : String(c)} />
                                </div>
                              </li>
                            ))}</ul>
                          ) : <span className="text-[#30302F]/50 dark:text-white/50 italic">No acceptance criteria defined yet.</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: Meta */}
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-[#30302F] dark:text-white">Priority</span>
                      {childEditing ? (
                        <CustomSelect
                          value={childEditData.urgency || 'P1'}
                          onChange={e => setChildEditData({...childEditData, urgency: e.target.value})}
                          options={['P0','P1','P2','P3','P4'].map(p => ({ value: p, label: p }))}
                          className="mt-2"
                          data-testid="child-edit-priority"
                        />
                      ) : (
                        <div className="mt-2">
                          <Badge className={`rounded-none text-[11px] uppercase px-2 py-0.5 font-semibold ${urgencyColors[selectedChild.urgency || 'P1']}`}>
                            {selectedChild.urgency || 'P1'}
                          </Badge>
                        </div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-[#30302F] dark:text-white">Assignee</span>
                      {childEditing ? (
                        <CustomSelect
                          value={childEditData.assignee || ''}
                          onChange={e => setChildEditData({...childEditData, assignee: e.target.value})}
                          options={[
                            { value: '', label: 'Unassigned' },
                            ...teamMembers.map(m => ({ value: m, label: m }))
                          ]}
                          placeholder="Select assignee..."
                          className="mt-2"
                          data-testid="child-edit-assignee"
                        />
                      ) : (
                        <div className="mt-2 text-sm text-[#30302F] dark:text-white">{selectedChild.assignee || '—'}</div>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-[#30302F] dark:text-white">Est. Release Date</span>
                      {childEditing ? (
                        <input type="date" value={childEditData.erd || ''} onChange={e => setChildEditData({...childEditData, erd: e.target.value})}
                          className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white" data-testid="child-edit-erd" />
                      ) : (
                        <div className="mt-2 text-sm text-[#30302F] dark:text-white">{selectedChild.erd ? new Date(selectedChild.erd).toLocaleDateString() : '—'}</div>
                      )}
                    </div>
                    {selectedChild._type === 'story' && (
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">Linked Feature</span>
                        {childEditing ? (
                          <CustomSelect
                            value={childEditData.intake_id || ''}
                            onChange={e => setChildEditData({...childEditData, intake_id: e.target.value})}
                            options={[
                              { value: '', label: 'None' },
                              ...allFeatures
                                .filter(f => {
                                  if (f.parent_id === item?.id) return true;
                                  if (item?.product_id && f.product_id) return f.product_id === item.product_id;
                                  if (item?.product && f.product) return f.product === item.product;
                                  return false;
                                })
                                .map(f => ({ value: f.id, label: f.title }))
                            ]}
                            placeholder="Select feature..."
                            className="mt-2"
                            data-testid="child-edit-feature"
                          />
                        ) : (
                          <div className="mt-2 text-sm text-[#30302F] dark:text-white">
                            {allFeatures.find(f => f.id === selectedChild.intake_id)?.title || '—'}
                          </div>
                        )}
                      </div>
                    )}
                    {selectedChild._type === 'intake' && (
                      <div>
                        <span className="text-sm font-medium text-[#30302F] dark:text-white">Product</span>
                        {childEditing ? (
                          <CustomSelect
                            value={childEditData.product_id || ''}
                            onChange={e => setChildEditData({...childEditData, product_id: e.target.value})}
                            options={[
                              { value: '', label: 'None' },
                              ...allProducts.map(p => ({ value: p.id, label: p.name }))
                            ]}
                            placeholder="Select product..."
                            className="mt-2"
                            data-testid="child-edit-product"
                          />
                        ) : (
                          <div className="mt-2 text-sm text-[#30302F] dark:text-white">
                            {allProducts.find(p => p.id === selectedChild.product_id)?.name || selectedChild.product || '—'}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm font-medium text-[#30302F] dark:text-white">Story Points</span>
                      {childEditing ? (
                        <input type="number" min={0} value={childEditData.story_points ?? ''} onChange={e => setChildEditData({...childEditData, story_points: e.target.value === '' ? null : parseInt(e.target.value, 10)})}
                          className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white" data-testid="child-edit-story-points" />
                      ) : (
                        <div className="mt-2 text-sm text-[#30302F] dark:text-white">{selectedChild.story_points ?? '—'}</div>
                      )}
                    </div>
                    <div>
                      <span className="text-sm font-medium text-[#30302F] dark:text-white">T-Shirt Size</span>
                      {childEditing ? (
                        <CustomSelect
                          value={childEditData.t_shirt_size || ''}
                          onChange={e => setChildEditData({...childEditData, t_shirt_size: e.target.value})}
                          options={[
                            { value: '', label: '—' },
                            { value: 'S', label: 'S (1-2 pts)' },
                            { value: 'M', label: 'M (3-5 pts)' },
                            { value: 'L', label: 'L (8 pts)' },
                            { value: 'XL', label: 'XL (13+ pts)' },
                          ]}
                          placeholder="Size..."
                          className="mt-2"
                          data-testid="child-edit-tshirt-size"
                        />
                      ) : (
                        <div className="mt-2 text-sm text-[#30302F] dark:text-white">{selectedChild.t_shirt_size || '—'}</div>
                      )}
                    </div>
                  </div>

                  {/* RICE Score — always visible */}
                  <div className="border-t border-[#e3e3e3] dark:border-white/10 pt-4">
                    <span className="text-sm font-medium text-[#30302F] dark:text-white">RICE Score</span>
                    {childEditing ? (
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {['rice_reach','rice_impact','rice_confidence','rice_effort'].map(f => (
                          <div key={f}>
                            <label className="text-[11px] text-[#30302F] dark:text-white capitalize font-medium">{f.replace('rice_','')}</label>
                            <input type="number" min={1} max={10} value={childEditData[f] || ''} onChange={e => setChildEditData({...childEditData, [f]: e.target.value})}
                              className="w-full text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 p-1.5 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white text-center" />
                          </div>
                        ))}
                      </div>
                    ) : selectedChild.rice_score ? (
                      <div className="mt-2 flex items-center gap-3">
                        <span className="text-2xl font-heading text-[#98139C]">{selectedChild.rice_score}</span>
                        <span className="text-xs text-[#30302F] dark:text-white">
                          R:{selectedChild.rice_reach} I:{selectedChild.rice_impact} C:{selectedChild.rice_confidence} E:{selectedChild.rice_effort}
                        </span>
                      </div>
                    ) : (
                      <div className="mt-2 text-sm text-[#30302F] dark:text-white">Not scored — click Edit to add RICE values</div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create Feature/Story Modal */}
      <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
        <DialogContent className="max-w-2xl bg-white dark:bg-[#1a1a2e] border border-[#e3e3e3] dark:border-white/10 p-0" data-testid="create-modal">
          <div className="p-6">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-xl font-heading text-[#30302F] dark:text-white" style={{ letterSpacing: '-0.4px' }}>
                {createType === 'feature' ? 'Create Feature' : 'Create Story'}
              </DialogTitle>
              <p className="text-sm text-[#30302F]/80 dark:text-white/80 mt-1">
                {createType === 'feature' 
                  ? `This feature will be linked to "${item?.title}"`
                  : `This story will be linked to "${item?.title}"`}
              </p>
            </DialogHeader>

            <div className="space-y-4">
              {/* Title - Common for both */}
              <div>
                <label className="text-sm font-medium text-[#30302F] dark:text-white">Title *</label>
                <input
                  type="text"
                  value={createData.title}
                  onChange={e => setCreateData({ ...createData, title: e.target.value })}
                  placeholder={createType === 'feature' ? "Feature title..." : "Story title..."}
                  className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                  data-testid="create-title-input"
                />
              </div>

              {createType === 'feature' ? (
                /* Feature-specific fields */
                <>
                  <div>
                    <label className="text-sm font-medium text-[#30302F] dark:text-white">Problem Statement</label>
                    <textarea
                      value={createData.problem}
                      onChange={e => setCreateData({ ...createData, problem: e.target.value })}
                      placeholder="What problem does this feature solve?"
                      className="w-full mt-2 min-h-[80px] px-3 py-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none"
                      data-testid="create-problem-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#30302F] dark:text-white">Users Affected</label>
                    <input
                      type="text"
                      value={createData.users_affected}
                      onChange={e => setCreateData({ ...createData, users_affected: e.target.value })}
                      placeholder="Who will use this feature?"
                      className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                      data-testid="create-users-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#30302F] dark:text-white">Success Criteria</label>
                    <textarea
                      value={createData.success_criteria}
                      onChange={e => setCreateData({ ...createData, success_criteria: e.target.value })}
                      placeholder="How will we know this feature is successful?"
                      className="w-full mt-2 min-h-[60px] px-3 py-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none"
                      data-testid="create-success-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#30302F] dark:text-white">Priority</label>
                    <CustomSelect
                      value={createData.urgency}
                      onChange={e => setCreateData({ ...createData, urgency: e.target.value })}
                      options={['P0', 'P1', 'P2', 'P3', 'P4'].map(p => ({ value: p, label: p }))}
                      className="mt-2"
                      data-testid="create-priority-select"
                    />
                  </div>
                </>
              ) : (
                /* Story-specific fields */
                <>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm font-medium text-[#30302F] dark:text-white">As a...</label>
                      <input
                        type="text"
                        value={createData.as_a}
                        onChange={e => setCreateData({ ...createData, as_a: e.target.value })}
                        placeholder="user role"
                        className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                        data-testid="create-asa-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#30302F] dark:text-white">I want...</label>
                      <input
                        type="text"
                        value={createData.i_want}
                        onChange={e => setCreateData({ ...createData, i_want: e.target.value })}
                        placeholder="action/feature"
                        className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                        data-testid="create-iwant-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium text-[#30302F] dark:text-white">So that...</label>
                      <input
                        type="text"
                        value={createData.so_that}
                        onChange={e => setCreateData({ ...createData, so_that: e.target.value })}
                        placeholder="benefit"
                        className="w-full mt-2 h-10 px-3 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white"
                        data-testid="create-sothat-input"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#30302F] dark:text-white">Acceptance Criteria (one per line)</label>
                    <textarea
                      value={createData.acceptance_criteria}
                      onChange={e => setCreateData({ ...createData, acceptance_criteria: e.target.value })}
                      placeholder="Given... When... Then...&#10;Users can...&#10;System should..."
                      className="w-full mt-2 min-h-[100px] px-3 py-2 text-sm bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 focus:outline-none focus:ring-2 focus:ring-[#98139C] text-[#30302F] dark:text-white resize-none font-mono"
                      data-testid="create-ac-input"
                    />
                  </div>
                </>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-sm font-medium border border-[#e3e3e3] dark:border-white/10 rounded-full hover:border-[#98139C] transition-colors text-[#30302F] dark:text-white"
                data-testid="create-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !createData.title.trim()}
                className="px-4 py-2 text-sm font-medium bg-[#98139C] text-white rounded-full hover:bg-[#7a0f7d] transition-colors disabled:opacity-50"
                data-testid="create-submit-btn"
              >
                {creating ? 'Creating...' : `Create ${createType === 'feature' ? 'Feature' : 'Story'}`}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
